// Pure motherboard orientation authority. No Three.js dependency.
//
// IMPORTANT: the cleaned motherboard reference is authored in a conventional front-view
// frame: source X = visual RIGHT, source Y = TOP, source Z = component side/out of PCB.
// The simulator PCB lies in local X/Z with local +Y as its component normal. Preserving
// chirality while rotating that reference into the simulator therefore maps source X to
// local -X, source Y to local +Z and source Z to local +Y.
//
// Canonical motherboard-local axes after that proper rotation:
//   +X = physical LEFT / rear-I/O edge
//   -X = physical RIGHT / DIMM edge
//   +Y = component side / CPU-cooler normal
//   +Z = physical TOP/EPS edge
//   -Z = primary-PCIe/bottom edge
//
// CPU, DIMM and PCIe landmarks NEVER move independently by case. Layouts choose one rigid
// SO(3) pose for the complete board. The two rear-I/O poses are the normal physical states:
//   class 23: component side -X, I/O rear, PCIe down
//   class 19: component side +X, I/O rear, PCIe up (180° in-plane relative to class 23)
// The front-I/O pair remains available for genuine front-I/O/inverted mount metadata.
export const MOTHERBOARD_ORIENTATION = Object.freeze({
  componentSidePositiveX: Object.freeze({
    ioRear: 19,
    ioFront: 21
  }),
  componentSideNegativeX: Object.freeze({
    ioRear: 23,
    ioFront: 17
  })
});

// Compatibility export for older UI/profile consumers. "Top up" alone is NOT enough to
// establish a physically correct board mount because it can point rear I/O toward the front.
export const MOTHERBOARD_TOP_UP_ORIENTATION = Object.freeze({
  componentSidePositiveX: MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioFront,
  componentSideNegativeX: MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioRear
});

export function normalizeMotherboardIoEdge(value='rear') {
  const raw=String(value ?? '').trim().toLowerCase().replace(/[_-]+/g,' ');
  if (!raw) return 'rear';
  if (/(^|\s)(front|forward)(\s|$)/.test(raw) || raw.includes('io front')) return 'front';
  return 'rear';
}

export function normalizeMotherboardPcieEdge(value='bottom') {
  const raw=String(value ?? '').trim().toLowerCase().replace(/[_-]+/g,' ');
  if (!raw) return 'bottom';
  if (/(^|\s)(top|upper|up)(\s|$)/.test(raw) || raw.includes('pcie up') || raw.includes('slot up')) return 'top';
  if (raw.includes('inverted') || raw.includes('rotate 180') || raw.includes('180')) return 'top';
  return 'bottom';
}

// Primary semantic selector: which way the component side points and whether the rear-I/O
// edge exits at the case rear/front. This is more stable than selecting by a visual quarter
// turn because PCIe-up/down then follows from rigid-body geometry automatically.
export function motherboardOrientationClass(componentNormalX = -1, ioEdge = 'rear') {
  const side = Number(componentNormalX) < 0 ? 'componentSideNegativeX' : 'componentSidePositiveX';
  const edge = normalizeMotherboardIoEdge(ioEdge) === 'front' ? 'ioFront' : 'ioRear';
  return MOTHERBOARD_ORIENTATION[side][edge];
}

// Explicit PCIe-edge compatibility helper for rare metadata. Because the board is rigid,
// choosing PCIe top/bottom together with component normal also determines rear-vs-front I/O.
export function motherboardOrientationForPcieEdge(componentNormalX = -1, pcieEdge = 'bottom') {
  const negative=Number(componentNormalX)<0;
  const top=normalizeMotherboardPcieEdge(pcieEdge)==='top';
  if(negative) return top ? MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioFront : MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioRear;
  return top ? MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioRear : MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioFront;
}

export function motherboardTopUpOrientationClass(componentNormalX = -1) {
  return motherboardOrientationForPcieEdge(componentNormalX,'bottom');
}

export function motherboardOrientationSemantics(classId) {
  const id=Number(classId);
  if (id===19) return Object.freeze({componentNormalX:1,ioEdge:'rear',pcieEdge:'top',topDirectionY:-1});
  if (id===21) return Object.freeze({componentNormalX:1,ioEdge:'front',pcieEdge:'bottom',topDirectionY:1});
  if (id===23) return Object.freeze({componentNormalX:-1,ioEdge:'rear',pcieEdge:'bottom',topDirectionY:1});
  if (id===17) return Object.freeze({componentNormalX:-1,ioEdge:'front',pcieEdge:'top',topDirectionY:-1});
  return null;
}
